import * as Sentry from '@sentry/vue';
import { ApiError } from '~~/types/ApiError';

export type ApiErrorSource = 'query' | 'mutation' | 'direct';

export const endpointKey = ({ method, schemaPath }: { method: string; schemaPath: string }) => `${method} ${schemaPath}`;

export type CaptureApiErrorOptions = {
	error: unknown;
	source: ApiErrorSource;
	tags?: Record<string, string | number | boolean>;
	contexts?: Record<string, Record<string, unknown>>;
};

/** Sentry titles an event `${type}: ${value}`, and the API's detail message alone does not name the endpoint. */
const withEndpointInTitle = (error: ApiError) => (event: Sentry.Event) => {
	// Linked errors get prepended, so the error we captured is the last entry.
	const exception = event.exception?.values?.at(-1);

	if (exception) {
		exception.value = `${endpointKey(error)} → ${error.status}: ${error.message}`;
	}

	return event;
};

export const captureApiError = ({ error, source, tags, contexts }: CaptureApiErrorOptions) => {
	Sentry.withScope((scope) => {
		scope.setTag('api_source', source);

		Object.entries(tags ?? {}).forEach(([tag, value]) => scope.setTag(tag, value));
		Object.entries(contexts ?? {}).forEach(([context, value]) => scope.setContext(context, value));

		if (error instanceof ApiError) {
			scope.setTag('http_status', error.status);
			scope.setTag('api_endpoint', endpointKey(error));
			scope.setContext('API Details', { url: error.url, error_data: error.data });
			scope.addEventProcessor(withEndpointInTitle(error));
			scope.setFingerprint(['api-error', error.method, error.schemaPath, String(error.status)]);
		}

		Sentry.captureException(error);
	});
};
